import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ImageOff, Rocket, Trash2, Undo2, UploadCloud } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Textarea } from '@/components/textarea';
import { TagsInput } from '@/components/tags-input';
import { StatusPill } from '@/components/status-dot';
import { SkeletonList } from '@/components/skeleton';
import { PageHeader } from '@/components/page-header';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import { RichTextEditor } from '@/components/editor/rich-text-editor';
import {
  useCreatePost,
  useDeleteImage,
  useGoLive,
  usePost,
  useRevertPost,
  useSubmitPost,
  useUnpublish,
  useUpdatePost,
  useUploadImage,
} from '@/lib/blog';
import { isAdminSurface } from '@/lib/surface';
import type { BlogPostData } from '@/types/api';

type Draft = {
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  tags: string[];
  seo_title: string;
  seo_description: string;
  author_name: string;
};

const EMPTY: Draft = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  tags: [],
  seo_title: '',
  seo_description: '',
  author_name: '',
};

function toDraft(post: BlogPostData): Draft {
  return {
    title: post.title ?? '',
    slug: post.slug ?? '',
    excerpt: post.excerpt ?? '',
    body: post.body ?? '',
    tags: post.tags ?? [],
    seo_title: post.seo_title ?? '',
    seo_description: post.seo_description ?? '',
    author_name: post.author_name ?? '',
  };
}

export function BlogEditor() {
  const { postId } = useParams();
  const [params] = useSearchParams();
  const siteId = Number(params.get('site'));
  const navigate = useNavigate();

  const id = postId ? Number(postId) : undefined;
  const existing = usePost(siteId || undefined, id);

  if (!siteId) {
    navigate('/blog', { replace: true });
    return null;
  }
  if (id && existing.isLoading) {
    return <SkeletonList rows={6} />;
  }

  return (
    <EditorForm
      key={id ?? 'new'}
      siteId={siteId}
      post={existing.data}
    />
  );
}

function EditorForm({ siteId, post }: { siteId: number; post?: BlogPostData }) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { error, wrap } = useFormError();

  const [draft, setDraft] = useState<Draft>(post ? toDraft(post) : EMPTY);
  const [postId, setPostId] = useState<number | undefined>(post?.id);
  const [pending, setPending] = useState(false);

  // Once we have a live/current copy of the post, keep its editorial state in
  // view so the action bar reflects reality after each mutation.
  const [state, setState] = useState({
    status: post?.status ?? 'draft',
    isLive: post?.is_live ?? false,
    featuredUrl: post?.featured_image_url ?? null,
    featuredMediaId: featuredMediaId(post),
  });

  useEffect(() => {
    if (post) {
      setDraft(toDraft(post));
      setPostId(post.id);
      setState({
        status: post.status,
        isLive: post.is_live,
        featuredUrl: post.featured_image_url,
        featuredMediaId: featuredMediaId(post),
      });
    }
  }, [post]);

  const create = useCreatePost(siteId);
  const update = useUpdatePost(siteId, postId ?? 0);
  const upload = useUploadImage(siteId, postId ?? 0);
  const removeImage = useDeleteImage(siteId, postId ?? 0);
  const submit = useSubmitPost(siteId);
  const revert = useRevertPost(siteId);
  const goLive = useGoLive(siteId);
  const unpublish = useUnpublish(siteId);

  const isNew = postId == null;

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function absorb(saved: BlogPostData) {
    setPostId(saved.id);
    setState({
      status: saved.status,
      isLive: saved.is_live,
      featuredUrl: saved.featured_image_url,
      featuredMediaId: featuredMediaId(saved),
    });
    setDraft((prev) => ({ ...prev, slug: saved.slug }));
  }

  async function save(): Promise<BlogPostData | null> {
    let result: BlogPostData | null = null;
    setPending(true);
    const ok = await wrap(async () => {
      const payload = { ...draft, slug: draft.slug || undefined };
      result = isNew
        ? await create.mutateAsync(payload)
        : await update.mutateAsync(payload);
    });
    setPending(false);
    if (ok && result) {
      absorb(result);
      if (isNew) {
        // Move from /blog/new to the post's stable URL without a reload.
        navigate(`/blog/${(result as BlogPostData).id}?site=${siteId}`, { replace: true });
      }
    }
    return ok ? result : null;
  }

  async function uploadFeatured(file: File) {
    if (postId == null) {
      const saved = await save();
      if (!saved) return;
    }
    const res = await upload.mutateAsync({ file, collection: 'featured' });
    absorb(res.post);
    toast.success('Featured image updated');
  }

  async function uploadInline(file: File): Promise<string> {
    const res = await upload.mutateAsync({ file, collection: 'images' });
    return res.image.url;
  }

  const canEdit = !state.isLive || state.status === 'submitted' || !isAdminSurface;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <button
        type="button"
        onClick={() => navigate(`/blog?site=${siteId}`)}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]"
      >
        <ArrowLeft className="size-4" /> Back to posts
      </button>

      <PageHeader
        title={isNew ? 'New post' : draft.title || 'Untitled'}
        badge={<StatePills status={state.status} isLive={state.isLive} />}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => void save()} disabled={pending}>
              {pending ? 'Saving…' : 'Save draft'}
            </Button>
            {renderPrimaryAction()}
          </div>
        }
      />

      <div className="space-y-4">
        <Field label="Title">
          <Input
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="A clear, compelling headline"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Slug (URL)">
            <Input
              value={draft.slug}
              onChange={(e) => set('slug', e.target.value)}
              placeholder="auto from title"
            />
          </Field>
          <Field label="Author">
            <Input
              value={draft.author_name}
              onChange={(e) => set('author_name', e.target.value)}
              placeholder="Byline (optional)"
            />
          </Field>
        </div>

        <Field label="Featured image">
          <FeaturedImage
            url={state.featuredUrl}
            uploading={upload.isPending}
            onUpload={uploadFeatured}
            onRemove={
              state.featuredMediaId != null
                ? async () => {
                    await removeImage.mutateAsync(state.featuredMediaId!);
                    setState((s) => ({ ...s, featuredUrl: null, featuredMediaId: null }));
                    toast.success('Featured image removed');
                  }
                : undefined
            }
          />
        </Field>

        <Field label="Excerpt">
          <Textarea
            value={draft.excerpt}
            onChange={(e) => set('excerpt', e.target.value)}
            placeholder="A one or two sentence summary for listings and previews."
            className="min-h-[64px]"
          />
        </Field>

        <Field label="Body">
          <RichTextEditor
            value={draft.body}
            onChange={(html) => set('body', html)}
            onUploadImage={postId != null ? uploadInline : undefined}
            disabled={!canEdit}
          />
          {postId == null ? (
            <p className="mt-1 text-xs text-[var(--color-foreground-subtle)]">
              Save a draft to enable image uploads.
            </p>
          ) : null}
        </Field>

        <Field label="Tags">
          <TagsInput value={draft.tags} onChange={(t) => set('tags', t)} />
        </Field>

        <details className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium text-[var(--color-foreground-muted)]">
            SEO
          </summary>
          <div className="mt-3 space-y-4">
            <Field label="SEO title">
              <Input
                value={draft.seo_title}
                onChange={(e) => set('seo_title', e.target.value)}
                placeholder="Defaults to the post title"
              />
            </Field>
            <Field label="Meta description">
              <Textarea
                value={draft.seo_description}
                onChange={(e) => set('seo_description', e.target.value)}
                placeholder="~155 characters for search results"
                className="min-h-[64px]"
              />
            </Field>
          </div>
        </details>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      </div>
    </div>
  );

  function renderPrimaryAction() {
    // Portal: submit / withdraw. Admin: go live / unpublish.
    if (!isAdminSurface) {
      if (state.status === 'submitted') {
        return (
          <Button
            variant="secondary"
            onClick={async () => {
              const saved = await save();
              if (saved == null && postId == null) return;
              await revert.mutateAsync(postId!);
              setState((s) => ({ ...s, status: 'draft' }));
              toast.success('Moved back to draft');
            }}
          >
            <Undo2 className="size-4" /> Withdraw
          </Button>
        );
      }
      return (
        <Button
          onClick={async () => {
            const saved = await save();
            const targetId = saved?.id ?? postId;
            if (targetId == null) return;
            await submit.mutateAsync(targetId);
            setState((s) => ({ ...s, status: 'submitted' }));
            toast.success('Submitted for publishing', 'The Mehrutherm team has been notified.');
          }}
        >
          <CheckCircle2 className="size-4" /> Submit for publishing
        </Button>
      );
    }

    // Admin surface.
    return (
      <>
        {state.status === 'submitted' ? (
          <Button
            onClick={async () => {
              const saved = await save();
              const targetId = saved?.id ?? postId;
              if (targetId == null) return;
              await goLive.mutateAsync(targetId);
              setState((s) => ({ ...s, isLive: true }));
              toast.success('Published to the live channel');
            }}
          >
            <Rocket className="size-4" /> Go live
          </Button>
        ) : null}
        {state.isLive ? (
          <Button
            variant="secondary"
            onClick={async () => {
              const ok = await confirm({
                title: 'Unpublish this post?',
                body: 'It will be removed from the live channel. The post itself is kept.',
                confirmLabel: 'Unpublish',
                tone: 'danger',
              });
              if (ok && postId != null) {
                await unpublish.mutateAsync(postId);
                setState((s) => ({ ...s, isLive: false }));
                toast.success('Removed from the live channel');
              }
            }}
          >
            <Undo2 className="size-4" /> Unpublish
          </Button>
        ) : null}
      </>
    );
  }
}

function featuredMediaId(post?: BlogPostData): number | null {
  if (!post) return null;
  const featured = post.images.find((img) => img.url === post.featured_image_url);
  return featured?.id ?? null;
}

function StatePills({ status, isLive }: { status: string; isLive: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {status === 'submitted' ? <StatusPill tone="info">Submitted</StatusPill> : null}
      {isLive ? (
        <StatusPill tone="success">Live</StatusPill>
      ) : status === 'draft' ? (
        <StatusPill tone="neutral">Draft</StatusPill>
      ) : null}
    </span>
  );
}

function FeaturedImage({
  url,
  uploading,
  onUpload,
  onRemove,
}: {
  url: string | null;
  uploading: boolean;
  onUpload: (file: File) => void | Promise<void>;
  onRemove?: () => void;
}) {
  if (url) {
    return (
      <div className="group relative w-full max-w-md overflow-hidden rounded-lg border border-[var(--color-border)]">
        <img src={url} alt="Featured" className="aspect-[1200/630] w-full object-cover" />
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            <Trash2 className="size-3.5" /> Remove
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <label className="flex w-full max-w-md cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-8 text-center text-sm text-[var(--color-foreground-muted)] hover:border-[var(--color-ring)]">
      {uploading ? (
        <>
          <UploadCloud className="size-5 animate-pulse" />
          Uploading…
        </>
      ) : (
        <>
          <ImageOff className="size-5" />
          Click to upload a featured image
        </>
      )}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onUpload(file);
          e.target.value = '';
        }}
      />
    </label>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}
